/**
 * Mock data factory for the demo walkthrough.
 * Returns a complete Group object with the current user as organizer.
 * All amounts are in pennies.
 */

export const DEMO_GROUP_ID = 'demo-group'

export function isDemoGroup(groupId?: string): boolean {
    return groupId === 'demo' || groupId === DEMO_GROUP_ID
}

export function buildDemoGroup(userId?: string | null): Group {
    const organizerId = userId || 'demo-organizer'

    const pledges: Pledge[] = [
        {
            id: 'demo-pledge-1',
            name: 'You',
            amount: 15000,
            additionalCharge: false,
            additionalCharges: [],
            availableAmount: 6137,
            chargedToCard: 6863,
            closedAt: '0001-01-01T00:00:00Z',
            createdAt: '2026-03-10T10:00:00Z',
            updatedAt: '2026-03-10T10:00:00Z',
            user: { id: organizerId, givenName: 'You', familyName: '', verified: true },
            transactionId: 'demo-si-1',
            paymentMethod: { type: 'credit_card', cardType: 'visa', lastFour: '4242', expirationMonth: '12', expirationYear: '28', token: '' },
        },
        {
            id: 'demo-pledge-2',
            name: 'Jordan S.',
            amount: 12500,
            additionalCharge: false,
            additionalCharges: [],
            availableAmount: 5781,
            chargedToCard: 5719,
            closedAt: '0001-01-01T00:00:00Z',
            createdAt: '2026-03-10T11:00:00Z',
            updatedAt: '2026-03-10T11:00:00Z',
            user: { id: 'demo-user-2', givenName: 'Jordan', familyName: 'S.', verified: true },
            transactionId: 'demo-si-2',
            paymentMethod: { type: 'credit_card', cardType: 'mastercard', lastFour: '8210', expirationMonth: '06', expirationYear: '27', token: '' },
        },
        {
            id: 'demo-pledge-3',
            name: 'Casey L.',
            amount: 12500,
            additionalCharge: false,
            additionalCharges: [],
            availableAmount: 5781,
            chargedToCard: 5719,
            closedAt: '0001-01-01T00:00:00Z',
            createdAt: '2026-03-11T09:00:00Z',
            updatedAt: '2026-03-11T09:00:00Z',
            user: { id: 'demo-user-3', givenName: 'Casey', familyName: 'L.', verified: true },
            transactionId: 'demo-si-3',
            paymentMethod: { type: 'credit_card', cardType: 'amex', lastFour: '1003', expirationMonth: '09', expirationYear: '28', token: '' },
        },
        {
            id: 'demo-pledge-4',
            name: 'Riley T.',
            amount: 10000,
            additionalCharge: false,
            additionalCharges: [],
            availableAmount: 4426,
            chargedToCard: 4574,
            closedAt: '0001-01-01T00:00:00Z',
            createdAt: '2026-03-11T14:00:00Z',
            updatedAt: '2026-03-11T14:00:00Z',
            user: { id: 'demo-user-4', givenName: 'Riley', familyName: 'T.', verified: true },
            transactionId: 'demo-si-4',
            paymentMethod: { type: 'credit_card', cardType: 'visa', lastFour: '7631', expirationMonth: '03', expirationYear: '29', token: '' },
        },
    ]

    // Keys must match ledger transactionId values — LedgerMerchantCell looks up via group.transactions[item.transactionId]
    const transactions: TransactionKey = {
        'demo-txn-1': [
            {
                id: 'demo-txn-1',
                status: 'finalized',
                createdAt: '2026-03-12T18:30:00Z',
                updatedAt: '2026-03-12T18:30:00Z',
                transactedAt: '2026-03-12T18:30:00Z',
                declineCode: '',
                declineMessage: '',
                category: { id: 'cat-1', name: 'Hospitality' },
                mcc: { category: 'hospitality', group: 'Lodging', number: '7011', description: 'Hotels and Motels' },
                merchant: { id: 'merch-1', name: 'Airbnb', address: '123 Beach Rd', city: 'Miami', state: 'FL', zipCode: '33139' },
                amount: 17500,
                preAuthAmount: 17500,
                postedAmount: 17500,
            },
        ],
        'demo-txn-2': [
            {
                id: 'demo-txn-2',
                status: 'finalized',
                createdAt: '2026-03-12T20:15:00Z',
                updatedAt: '2026-03-12T20:15:00Z',
                transactedAt: '2026-03-12T20:15:00Z',
                declineCode: '',
                declineMessage: '',
                category: { id: 'cat-2', name: 'Transportation' },
                mcc: { category: 'transportation', group: 'Rideshare', number: '4121', description: 'Taxicabs and Rideshares' },
                merchant: { id: 'merch-2', name: 'Uber', address: '', city: 'Miami', state: 'FL', zipCode: '33139' },
                amount: 4750,
                preAuthAmount: 4750,
                postedAmount: 4750,
            },
        ],
        'demo-txn-3': [
            {
                id: 'demo-txn-3',
                status: 'finalized',
                createdAt: '2026-03-13T12:45:00Z',
                updatedAt: '2026-03-13T12:45:00Z',
                transactedAt: '2026-03-13T12:45:00Z',
                declineCode: '',
                declineMessage: '',
                category: { id: 'cat-3', name: 'Restaurants' },
                mcc: { category: 'restaurants', group: 'Restaurants', number: '5812', description: 'Eating Places' },
                merchant: { id: 'merch-3', name: 'Chipotle', address: '456 Ocean Dr', city: 'Miami', state: 'FL', zipCode: '33139' },
                amount: 625,
                preAuthAmount: 625,
                postedAmount: 625,
            },
        ],
    }

    // BreakdownItem.id must match Pledge.id — the close-group component uses this to find the pledge
    const ledger: LedgerLineItem[] = [
        {
            transactionId: 'demo-txn-1',
            breakdown: [
                { id: 'demo-pledge-1', userId: organizerId, givenName: 'You', familyName: '', amount: 5250 },
                { id: 'demo-pledge-2', userId: 'demo-user-2', givenName: 'Jordan', familyName: 'S.', amount: 4375 },
                { id: 'demo-pledge-3', userId: 'demo-user-3', givenName: 'Casey', familyName: 'L.', amount: 4375 },
                { id: 'demo-pledge-4', userId: 'demo-user-4', givenName: 'Riley', familyName: 'T.', amount: 3500 },
            ],
        },
        {
            transactionId: 'demo-txn-2',
            breakdown: [
                { id: 'demo-pledge-1', userId: organizerId, givenName: 'You', familyName: '', amount: 1425 },
                { id: 'demo-pledge-2', userId: 'demo-user-2', givenName: 'Jordan', familyName: 'S.', amount: 1188 },
                { id: 'demo-pledge-3', userId: 'demo-user-3', givenName: 'Casey', familyName: 'L.', amount: 1188 },
                { id: 'demo-pledge-4', userId: 'demo-user-4', givenName: 'Riley', familyName: 'T.', amount: 949 },
            ],
        },
        {
            transactionId: 'demo-txn-3',
            breakdown: [
                { id: 'demo-pledge-1', userId: organizerId, givenName: 'You', familyName: '', amount: 188 },
                { id: 'demo-pledge-2', userId: 'demo-user-2', givenName: 'Jordan', familyName: 'S.', amount: 156 },
                { id: 'demo-pledge-3', userId: 'demo-user-3', givenName: 'Casey', familyName: 'L.', amount: 156 },
                { id: 'demo-pledge-4', userId: 'demo-user-4', givenName: 'Riley', familyName: 'T.', amount: 125 },
            ],
        },
    ]

    const card: GroupCard = {
        id: 'demo-card',
        cardLimit: 50000,
        cardNumber: '',
        cardType: 'virtual',
        cardSecurityCode: '',
        currentAmount: 27125,
        expirationMonth: '12',
        expirationYear: '2028',
        cvv: '',
        usedAmount: 22875,
        lastFour: '4821',
        formattedAmounts: {
            usedAmount: '$228.75',
            currentAmount: '$271.25',
            cardLimit: '$500.00',
        },
    }

    const activities: Activity[] = [
        { Type: 'card_transaction', Message: 'A purchase of $6.25 was made at Chipotle', Timestamp: '2026-03-13T12:45:00Z' },
        { Type: 'card_transaction', Message: 'A purchase of $47.50 was made at Uber', Timestamp: '2026-03-12T20:15:00Z' },
        { Type: 'card_transaction', Message: 'A purchase of $175.00 was made at Airbnb', Timestamp: '2026-03-12T18:30:00Z' },
        { Type: 'user_joined', Message: 'Riley T. joined the group', Timestamp: '2026-03-11T14:00:00Z' },
        { Type: 'user_joined', Message: 'Casey L. joined the group', Timestamp: '2026-03-11T09:00:00Z' },
        { Type: 'user_joined', Message: 'Jordan S. joined the group', Timestamp: '2026-03-10T11:00:00Z' },
        { Type: 'group_created', Message: 'You created the group', Timestamp: '2026-03-10T10:00:00Z' },
    ]

    return {
        id: DEMO_GROUP_ID,
        title: 'Spring Break Trip',
        type: 'fixed',
        address: {
            line1: '123 Demo St',
            city: 'Miami',
            state: 'FL',
            postal_code: '33139',
            country: 'US',
        },
        organizer: { id: organizerId, givenName: 'You', familyName: '', verified: true },
        targetAmount: 50000,
        collectedAmount: 50000,
        spendAmount: 22875,
        createdAt: '2026-03-10T10:00:00Z',
        updatedAt: '2026-03-13T12:45:00Z',
        closedAt: '2026-03-14T10:00:00Z',
        completedAt: '0001-01-01T00:00:00Z',
        canceledAt: '0001-01-01T00:00:00Z',
        finalizedAt: '0001-01-01T00:00:00Z',
        pledges,
        card,
        transactions,
        ledger,
        activities,
    }
}
